import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { TStore, useAppDispatch } from '../store';
import { getRecoveryData } from '../actions/recoveryAction';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Spin } from 'antd';
import { SleepAnalyzer, SleepReading, SleepStage, SleepMetrics } from '../utils/sleepAnalyzer';

const Sleep: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Use recovery data since sleep data is in the recovery table
  const { recoveryData, loading, error } = useSelector((state: TStore) => state.recovery);
  const selectedVessel = useSelector((state: TStore) => state.averageFleetDetails.selectedVessel);

  console.log(selectedVessel?.shipId);
  
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [viewMode, setViewMode] = useState<'score' | 'hours' | 'stages'>('score');

  // Fetch recovery data (which contains sleep data) on component mount and when vessel changes
  useEffect(() => {
    if (selectedVessel?.shipId) {
      dispatch(getRecoveryData(selectedVessel.shipId));
    } else {
      dispatch(getRecoveryData(1));
    }
  }, [dispatch, selectedVessel?.shipId]);

  // STEP 1: Filter data by selected vessel first (if applicable)
  const vesselFilteredData = React.useMemo(() => {
    if (!recoveryData || recoveryData.length === 0) return [];
    
    let vesselData = recoveryData;
    if (selectedVessel?.shipId) {
      vesselData = recoveryData.filter((item: SleepReading) => item.shipId === selectedVessel.shipId);
    }

    return vesselData;
  }, [recoveryData, selectedVessel?.shipId]);

  // STEP 2: Aggregate data by date (multiple users per date become one record per date)
  const aggregatedData = React.useMemo(() => {
    const aggregated = SleepAnalyzer.aggregateDataByDate(vesselFilteredData);
    console.log('Aggregated sleep data length:', aggregated.length);
    console.log('Sample aggregated sleep data:', aggregated.slice(0, 3));
    return aggregated;
  }, [vesselFilteredData]);

  // STEP 3: Filter by time range (7d, 30d, 90d)
  const filteredData = React.useMemo(() => {
    const timeFiltered = SleepAnalyzer.filterDataByTimeRange(aggregatedData, timeRange);
    console.log(`Time filtered sleep data (${timeRange}):`, timeFiltered.length);
    return timeFiltered;
  }, [aggregatedData, timeRange]);

  // Calculate metrics from real data - adjusted to handle available data
  const metrics: SleepMetrics = React.useMemo(() => {
    if (filteredData.length === 0) {
      return {
        averageDuration: 0,
        averageScore: 0,
        totalSleep: 0,
        sleepDebt: 0,
        trend: 'stable'
      };
    }

    // Only include non-null values in calculations
    const validScores = filteredData.filter(d => d.sleepScore > 0).map(d => d.sleepScore);
    const validDurations = filteredData.filter(d => d.hoursOfSleep > 0).map(d => d.hoursOfSleep);
    const validDebt = filteredData.filter(d => d.sleepDebtHours !== null && d.sleepDebtHours !== undefined).map(d => d.sleepDebtHours);

    return {
      averageDuration: validDurations.length > 0 ? validDurations.reduce((a, b) => a + b, 0) / validDurations.length : 0,
      averageScore: validScores.length > 0 ? validScores.reduce((a, b) => a + b, 0) / validScores.length : 0,
      totalSleep: validDurations.reduce((a, b) => a + b, 0),
      sleepDebt: validDebt.reduce((a, b) => a + b, 0),
      trend: SleepAnalyzer.calculateTrend(validScores, timeRange)
    };
  }, [filteredData]);

  const latestSleep = filteredData[filteredData.length - 1];
  console.log('Latest sleep data:', latestSleep);
  const currentSleepScore = selectedVessel?.averageSleep ?? latestSleep?.sleepScore ?? 0;
  const performanceZone = SleepAnalyzer.getPerformanceZone(currentSleepScore);

  const sleepStages: SleepStage[] = React.useMemo(() => {
    if (!latestSleep || !latestSleep.hoursOfSleep) return [];
    
    // Only show stages that have data
    const stages = [];
    
    if (latestSleep.deeplSleepHours > 0) {
      stages.push({
        name: 'Deep Sleep',
        duration: latestSleep.deeplSleepHours,
        percentage: (latestSleep.deeplSleepHours / latestSleep.hoursOfSleep) * 100,
        color: '#2E7D89',
        description: 'Physical restoration and immune system strengthening'
      });
    }
    
    if (latestSleep.remSleepHours > 0) {
      stages.push({
        name: 'REM Sleep',
        duration: latestSleep.remSleepHours,
        percentage: (latestSleep.remSleepHours / latestSleep.hoursOfSleep) * 100,
        color: '#4ECDC4',
        description: 'Memory consolidation and cognitive processing'
      });
    }
    
    if (latestSleep.lightSleepHours > 0) {
      stages.push({
        name: 'Light Sleep',
        duration: latestSleep.lightSleepHours,
        percentage: (latestSleep.lightSleepHours / latestSleep.hoursOfSleep) * 100,
        color: '#A8E6CF',
        description: 'Transition between wake and deeper sleep stages'
      });
    }
    
    if (latestSleep.awakeHours > 0) {
      stages.push({
        name: 'Awake',
        duration: latestSleep.awakeHours,
        percentage: (latestSleep.awakeHours / latestSleep.hoursOfSleep) * 100,
        color: '#FFD93D',
        description: 'Brief awakenings throughout the night'
      });
    }
    
    return stages;
  }, [latestSleep]);

  // Prepare chart data
  const chartData = filteredData.map(item => ({
    ...item,
    date: new Date(item.date).toISOString().split('T')[0]
  }));

  if (loading) {
    return (
      <div className="sleep-analysis">
        <Spin className='recovery-analysis__loading' />
      </div>
    );
  }

  if (error) {
    return (
      <div className="sleep-analysis">
        <div className="sleep-analysis__error">Error loading sleep data: {error}</div>
      </div>
    );
  }

  if (filteredData.length === 0) {
    return (
      <div className="sleep-analysis">
        <div className="sleep-analysis__no-data">
          No sleep data available for the selected time range and vessel.
        </div>
      </div>
    );
  }

  return (
    <div className="sleep-analysis">
      {/* Header */}
      <div className="sleep-analysis__header">
        <div className="sleep-analysis__title-section">
          <h1 className="sleep-analysis__title">Sleep Analysis</h1>
          <p className="sleep-analysis__subtitle">
            Comprehensive sleep tracking including duration, score, and available sleep stages
          </p>
        </div>
        
        <div className="sleep-analysis__controls">
          <div className="control-group" role="group" aria-label="Time range">
            <button
              className={`control-btn ${timeRange === '7d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('7d')}
              aria-pressed={timeRange === '7d'}
            >
              7D
            </button>
            <button
              className={`control-btn ${timeRange === '30d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('30d')}
              aria-pressed={timeRange === '30d'}
            >
              30D
            </button>
            <button
              className={`control-btn ${timeRange === '90d' ? 'control-btn--active' : ''}`}
              onClick={() => setTimeRange('90d')}
              aria-pressed={timeRange === '90d'}
            >
              90D
            </button>
          </div>

          <div className="control-group" role="group" aria-label="View mode">
            <button
              className={`control-btn ${viewMode === 'score' ? 'control-btn--active' : ''}`}
              onClick={() => setViewMode('score')}
              aria-pressed={viewMode === 'score'}
            >
              Sleep Score
            </button>
            <button
              className={`control-btn ${viewMode === 'hours' ? 'control-btn--active' : ''}`}
              onClick={() => setViewMode('hours')}
              aria-pressed={viewMode === 'hours'}
            >
              Sleep Hours
            </button>
            {sleepStages.length > 0 && (
              <button
                className={`control-btn ${viewMode === 'stages' ? 'control-btn--active' : ''}`}
                onClick={() => setViewMode('stages')}
                aria-pressed={viewMode === 'stages'}
              >
                Stages
              </button>
            )}
          </div>
          <button className="btn-back" onClick={() => navigate(-1)}>
            <span className="btn-back__arrow" aria-hidden="true">←</span>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Current Sleep Display */}
      <div className="sleep-analysis__current">
        <div className="current-sleep">
          <div className="sleep-score">
            <div className="sleep-score__value" style={{ color: performanceZone.color }}>
              {Math.round(metrics.averageScore)}
            </div>
            <div className="sleep-score__label">Sleep Score</div>
            <div className="sleep-score__zone" style={{ color: performanceZone.color }}>
              {performanceZone.label}
            </div>
            <div className="sleep-score__description">
              {performanceZone.description}
            </div>
          </div>
        </div>
        
        <div className="sleep-metrics">
          <div className="metric-card">
            <div className="metric-card__value">
              {SleepAnalyzer.formatTime(metrics.averageDuration)}
            </div>
            <div className="metric-card__label">Avg Duration</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">
              {Math.round(metrics.averageScore)}
            </div>
            <div className="metric-card__label">Avg Score</div>
          </div>
          <div className="metric-card">
            <div className="metric-card__value">
              <span className={`trend-indicator trend-indicator--${metrics.trend}`}>
                {metrics.trend === 'improving' ? '↗' : metrics.trend === 'declining' ? '↘' : '→'}
              </span>
            </div>
            <div className="metric-card__label">Trend</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="sleep-analysis__content">
        {viewMode === 'score' && (
          <div className="score-container">
            <div className="chart-section">
              <h3 className="chart-section__title">Sleep Score Over Time ({timeRange.toUpperCase()})</h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    domain={[0, 100]} 
                    tick={{ fontSize: 12 }}
                    label={{ value: 'Sleep Score', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value: any) => [value.toFixed(0), 'Sleep Score']}
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="sleepScore" 
                    stroke="#4ECDC4" 
                    strokeWidth={3}
                    dot={{ fill: '#4ECDC4', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#4ECDC4', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="chart-info">
                <p>Showing {chartData.length} days of sleep score data</p>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'hours' && (
          <div className="hours-container">
            <div className="duration-chart">
              <h3 className="duration-chart__title">Sleep Duration ({timeRange.toUpperCase()})</h3>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric'
                    })}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => SleepAnalyzer.formatTime(value)}
                    label={{ value: 'Hours of Sleep', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value: any) => [SleepAnalyzer.formatTime(value), 'Duration']}
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                  />
                  <Bar dataKey="hoursOfSleep" fill="#4ECDC4" />
                </BarChart>
              </ResponsiveContainer>
              <div className="chart-info">
                <p>Showing {chartData.length} days of sleep duration data</p>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'stages' && sleepStages.length > 0 && (
          <div className="stages-container">
            <div className="stages-pie">
              <h3 className="stages-pie__title">Last Night's Sleep Stages</h3>
              <div className="pie-chart-container">
                <ResponsiveContainer width={300} height={300}>
                  <PieChart>
                    <Pie
                      data={sleepStages}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="duration"
                    >
                      {sleepStages.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: any) => [SleepAnalyzer.formatTime(value), 'Duration']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pie-center">
                  <span className="pie-center__value">
                    {SleepAnalyzer.formatTime(latestSleep?.hoursOfSleep || 0)}
                  </span>
                  <span className="pie-center__label">Total Sleep</span>
                </div>
              </div>
            </div>

            <div className="stages-breakdown">
              <h3 className="stages-breakdown__title">Sleep Stage Breakdown</h3>
              <div className="stages-list">
                {sleepStages.map((stage, index) => (
                  <div key={index} className="stage-item">
                    <div className="stage-item__header">
                      <div className="stage-item__name">
                        <div 
                          className="stage-item__indicator" 
                          style={{ backgroundColor: stage.color }}
                        ></div>
                        {stage.name}
                      </div>
                      <div className="stage-item__stats">
                        <span className="stage-item__duration">
                          {SleepAnalyzer.formatTime(stage.duration)}
                        </span>
                        <span className="stage-item__percentage">
                          {stage.percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="stage-item__description">
                      {stage.description}
                    </div>
                    <div className="stage-item__progress">
                      <div 
                        className="stage-item__fill" 
                        style={{ 
                          width: `${stage.percentage}%`, 
                          backgroundColor: stage.color 
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sleep;